import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Clapperboard,
  Download,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreatorDashboardShell } from "@/components/CreatorDashboardShell";
import { cn } from "@/lib/utils";
import { api, ApiError, getAccessToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSeriesLive } from "@/hooks/use-series-live";
import {
  creatorEpisodeDownloadPath,
  creatorEpisodeProcessPath,
  creatorSeriesPath,
  env,
} from "@/lib/env";
import {
  fetchCreatorConfig,
  type CreatorConfig,
} from "@/lib/creator-config";
import type {
  SeriesDetail,
  SocialAccountSummary,
  TopicRow,
} from "@/lib/series-types";

export const Route = createFileRoute("/dashboard/series/$seriesId")({
  head: () => ({
    meta: [{ title: "Series — AutoTubber" }],
  }),
  component: SeriesDetailPage,
});

function statusTone(status: string) {
  if (status === "PUBLISHED") return "bg-primary/15 text-primary border-primary/40";
  if (status === "FAILED") return "bg-destructive/15 text-destructive border-destructive/40";
  if (status === "PENDING_APPROVAL") return "bg-secondary text-foreground border-border";
  return "bg-muted text-muted-foreground border-border";
}

function SeriesDetailPage() {
  const { seriesId } = Route.useParams();
  const navigate = useNavigate();
  const { ready, user } = useAuth();
  const [config, setConfig] = useState<CreatorConfig | null>(null);
  const [series, setSeries] = useState<SeriesDetail | null>(null);
  const [section, setSection] = useState<"topics" | "episodes" | "settings">(
    "topics",
  );
  const [newTopic, setNewTopic] = useState("");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [channels, setChannels] = useState<SocialAccountSummary[]>([]);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate({
        to: env.routes.login,
        search: { redirect: `/dashboard/series/${seriesId}` },
        replace: true,
      });
    }
  }, [ready, user, navigate, seriesId]);

  const load = useCallback(async () => {
    const [cfg, res, accountsRes] = await Promise.all([
      fetchCreatorConfig(),
      api<{ series: SeriesDetail }>(creatorSeriesPath(seriesId)),
      api<{ accounts: SocialAccountSummary[] }>(env.paths.creatorAccounts),
    ]);
    setConfig(cfg);
    setSeries(res.series);
    setChannels(accountsRes.accounts || []);
  }, [seriesId]);

  useEffect(() => {
    if (!ready || !user) return;
    load().catch((err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to load series");
      // Don't bounce to overview on a transient network blip.
      if (err instanceof ApiError && err.status === 0) return;
      navigate({ to: env.routes.dashboard });
    });
  }, [ready, user, load, navigate]);

  const refreshSeries = async () => {
    const res = await api<{ series: SeriesDetail }>(creatorSeriesPath(seriesId));
    setSeries(res.series);
  };

  useSeriesLive(seriesId, Boolean(ready && user && series), series, setSeries);

  const generateNextEpisode = async () => {
    setGenerating(true);
    try {
      const created = await api<{ episode: { id: string; status: string } }>(
        `${creatorSeriesPath(seriesId)}/episodes`,
        { method: "POST", body: {} },
      );
      await api(creatorEpisodeProcessPath(created.episode.id), {
        method: "POST",
      });
      toast.success(
        "Episode queued — processing started. Keep the worker / stack running.",
      );
      setSection("episodes");
      await refreshSeries();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not start generation",
      );
    } finally {
      setGenerating(false);
    }
  };

  const retryFailedEpisode = async (episodeId: string, topicTitle: string) => {
    setRetryingId(episodeId);
    try {
      await api(creatorEpisodeProcessPath(episodeId), { method: "POST" });
      toast.success(
        `Retrying “${topicTitle}” — will recover the finished video if MPT still has it.`,
      );
      setSection("episodes");
      await refreshSeries();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not retry episode",
      );
    } finally {
      setRetryingId(null);
    }
  };

  const downloadEpisodeVideo = async (
    episodeId: string,
    topicTitle: string,
  ) => {
    setDownloadingId(episodeId);
    try {
      const token = getAccessToken();
      const res = await fetch(
        `${env.apiUrl}${creatorEpisodeDownloadPath(episodeId)}`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(data.error || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const safe =
        topicTitle
          .replace(/[<>:"/\\|?*]/g, "")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 80) || "episode";
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${safe}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("Download started");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not download video",
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const addTopic = async () => {
    if (!newTopic.trim()) return;
    setSaving(true);
    try {
      await api(`${creatorSeriesPath(seriesId)}/topics`, {
        method: "POST",
        body: { title: newTopic.trim() },
      });
      setNewTopic("");
      await refreshSeries();
      toast.success("Topic added");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not add topic");
    } finally {
      setSaving(false);
    }
  };

  const removeTopic = async (topicId: string) => {
    setSaving(true);
    try {
      await api(`${creatorSeriesPath(seriesId)}/topics/${topicId}`, {
        method: "DELETE",
      });
      await refreshSeries();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not remove topic");
    } finally {
      setSaving(false);
    }
  };

  const moveTopic = async (index: number, direction: -1 | 1) => {
    if (!series) return;
    const next = index + direction;
    if (next < 0 || next >= series.topics.length) return;
    const topics = [...series.topics];
    const tmp = topics[index]!;
    topics[index] = topics[next]!;
    topics[next] = tmp;
    setSeries({ ...series, topics });
    setSaving(true);
    try {
      const res = await api<{ topics: TopicRow[] }>(
        `${creatorSeriesPath(seriesId)}/topics/reorder`,
        {
          method: "PUT",
          body: { topicIds: topics.map((t) => t.id) },
        },
      );
      setSeries({ ...series, topics: res.topics });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Reorder failed");
      await refreshSeries();
    } finally {
      setSaving(false);
    }
  };

  const patchSeries = async (body: Record<string, unknown>) => {
    setSaving(true);
    try {
      const res = await api<{ series: SeriesDetail }>(creatorSeriesPath(seriesId), {
        method: "PATCH",
        body,
      });
      setSeries(res.series);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!ready || !user || !series) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
        Loading series…
      </div>
    );
  }

  const schedule = series.postingSchedule || {};

  return (
    <CreatorDashboardShell
      config={config}
      activeTab="overview"
      userEmail={user.email}
      onTabChange={(tab) =>
        navigate({
          to: env.routes.dashboard,
          search: { tab },
        })
      }
    >
      <main className="mx-auto w-full max-w-none px-5 py-10 lg:px-8">
        <Button variant="ghost" size="sm" className="-ml-2 mb-4" asChild>
          <Link to={env.routes.dashboard} search={{ tab: "overview" }}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to series
          </Link>
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{series.name}</h1>
            <p className="mt-1 text-muted-foreground">
              {series.category} · {series.narrator.name} · {series.status}
            </p>
          </div>
          <Badge variant="outline">{series._count.topics} topics</Badge>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {(
            [
              ["topics", "Topic queue"],
              ["episodes", "Episode history"],
              ["settings", "Settings"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setSection(id)}
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm transition-colors",
                section === id
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-border text-muted-foreground hover:border-primary/50",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {section === "topics" && (
          <div className="mt-6 space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Add a topic title"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTopic()}
              />
              <Button onClick={addTopic} disabled={saving || !newTopic.trim()}>
                <Plus className="mr-1 h-4 w-4" /> Add
              </Button>
            </div>

            {series.topics.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border px-5 py-10 text-center text-sm text-muted-foreground">
                No topics yet. Add episode ideas to the queue.
              </p>
            ) : (
              <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                {series.topics.map((t, index) => (
                  <div
                    key={t.id}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        disabled={saving || index === 0}
                        onClick={() => moveTopic(index, -1)}
                        className="rounded p-1 text-muted-foreground hover:bg-secondary disabled:opacity-30"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={saving || index === series.topics.length - 1}
                        onClick={() => moveTopic(index, 1)}
                        className="rounded p-1 text-muted-foreground hover:bg-secondary disabled:opacity-30"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{t.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.used ? "Used" : "Queued"}
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={saving}
                      onClick={() => removeTopic(t.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {section === "episodes" && (
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 text-primary">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                  </span>
                  Live
                </span>
              </p>
              <Button
                disabled={
                  generating ||
                  saving ||
                  !series.topics.some((t) => !t.used)
                }
                onClick={() => void generateNextEpisode()}
              >
                <Clapperboard className="mr-1 h-4 w-4" />
                {generating ? "Starting…" : "Generate next episode"}
              </Button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3">Topic</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Video</th>
                    <th className="px-5 py-3">Error</th>
                    <th className="px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {series.episodes.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-10 text-center text-muted-foreground"
                      >
                        No episodes yet. Add topics, then generate the next
                        episode.
                      </td>
                    </tr>
                  ) : (
                    series.episodes.map((e) => (
                      <tr key={e.id} className="hover:bg-secondary/40">
                        <td className="px-5 py-4 font-medium">{e.topicTitle}</td>
                        <td className="px-5 py-4">
                          <Badge
                            variant="outline"
                            className={cn(
                              "font-normal",
                              statusTone(String(e.status)),
                            )}
                          >
                            {String(e.status).replaceAll("_", " ")}
                          </Badge>
                        </td>
                        <td className="px-5 py-4 text-muted-foreground">
                          {e.videoUrl ? (
                            <a
                              href={e.videoUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline"
                            >
                              Open
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="max-w-[220px] truncate px-5 py-4 text-xs text-destructive">
                          {e.errorMessage || "—"}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            {e.videoUrl ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Download video"
                                aria-label="Download video"
                                disabled={downloadingId === e.id}
                                onClick={() =>
                                  void downloadEpisodeVideo(
                                    e.id,
                                    e.topicTitle,
                                  )
                                }
                              >
                                <Download
                                  className={cn(
                                    "h-4 w-4",
                                    downloadingId === e.id && "animate-pulse",
                                  )}
                                />
                              </Button>
                            ) : null}
                            {e.status === "FAILED" ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={retryingId === e.id || generating}
                                onClick={() =>
                                  void retryFailedEpisode(e.id, e.topicTitle)
                                }
                              >
                                <RefreshCw
                                  className={cn(
                                    "mr-1 h-3.5 w-3.5",
                                    retryingId === e.id && "animate-spin",
                                  )}
                                />
                                {retryingId === e.id ? "Retrying…" : "Retry"}
                              </Button>
                            ) : !e.videoUrl ? (
                              <span className="text-muted-foreground">—</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {section === "settings" && (
          <div className="mt-6 space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div>
              <label className="text-sm font-medium">Series name</label>
              <Input
                className="mt-2"
                defaultValue={series.name}
                onBlur={(e) => {
                  if (e.target.value.trim() && e.target.value !== series.name) {
                    void patchSeries({ name: e.target.value.trim() });
                  }
                }}
              />
            </div>

            <div className="border-b border-border/60 pb-4">
              <p className="text-sm font-medium">Video type</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {series.videoType === "LONGFORM"
                  ? "Longform · landscape 16:9"
                  : "Shorts · vertical 9:16"}
                <span className="text-muted-foreground/80">
                  {" "}
                  (set during onboarding)
                </span>
              </p>
            </div>

            <div className="flex items-center justify-between border-b border-border/60 pb-4">
              <div>
                <p className="font-medium">Auto-publish</p>
                <p className="text-sm text-muted-foreground">
                  Off = episodes wait for approval.
                </p>
              </div>
              <Switch
                checked={series.autoPublish}
                disabled={saving}
                onCheckedChange={(v) => void patchSeries({ autoPublish: v })}
              />
            </div>

            <div>
              <p className="mb-2 text-sm font-medium">
                Post to channel (YouTube publish only for now)
              </p>
              <Select
                value={series.socialAccountId || "none"}
                onValueChange={(v) =>
                  void patchSeries({
                    socialAccountId: v === "none" ? null : v,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="No channel assigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No channel (assign later)</SelectItem>
                  {channels.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {(c.channelTitle || c.accountLabel) +
                        (c.platform ? ` · ${c.platform}` : "")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                Use different channels per series (crime, true stories, etc.).
                Connect channels under Dashboard → Accounts.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-medium">Status</p>
                <Select
                  value={series.status}
                  onValueChange={(v) => void patchSeries({ status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="PAUSED">PAUSED</SelectItem>
                    <SelectItem value="ARCHIVED">ARCHIVED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Voice (fixed)</p>
                <Select
                  value={series.voiceId}
                  onValueChange={(v) =>
                    void patchSeries({
                      voiceId: v,
                      voiceMode: "fixed",
                      voiceIds: [v],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.creatorVoices || config?.voices || []).map(
                      (v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                          {"gender" in v && v.gender
                            ? ` · ${String(v.gender)}`
                            : ""}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  For rotate/auto modes, use Dashboard → Settings.
                </p>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Narrator</p>
                <Select
                  value={series.narrator.slug}
                  onValueChange={(v) => void patchSeries({ narratorSlug: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.uiNarrators || []).map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Background</p>
                <Select
                  value={series.background.slug}
                  onValueChange={(v) => void patchSeries({ backgroundSlug: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.uiBackgrounds || []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <p className="mb-2 text-sm font-medium">Frequency</p>
                <Input
                  defaultValue={schedule.frequency || ""}
                  onBlur={(e) => {
                    if (!e.target.value.trim()) return;
                    void patchSeries({
                      postingSchedule: {
                        ...schedule,
                        frequency: e.target.value.trim(),
                        time: schedule.time || "09:00",
                        timezone: schedule.timezone || "UTC",
                      },
                    });
                  }}
                />
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Time</p>
                <Input
                  defaultValue={schedule.time || ""}
                  onBlur={(e) => {
                    if (!e.target.value.trim()) return;
                    void patchSeries({
                      postingSchedule: {
                        ...schedule,
                        frequency: schedule.frequency || "daily",
                        time: e.target.value.trim(),
                        timezone: schedule.timezone || "UTC",
                      },
                    });
                  }}
                />
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Timezone</p>
                <Input
                  defaultValue={schedule.timezone || ""}
                  onBlur={(e) => {
                    if (!e.target.value.trim()) return;
                    void patchSeries({
                      postingSchedule: {
                        ...schedule,
                        frequency: schedule.frequency || "daily",
                        time: schedule.time || "09:00",
                        timezone: e.target.value.trim(),
                      },
                    });
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </CreatorDashboardShell>
  );
}
